import { Group } from "@mantine/core";
import { IconMapPin, IconMicrophone, IconTag } from "@tabler/icons-react";
import { useMemo, useState } from "react";

import { useGetAllSpeakers } from "../../api/speakers";
import { useGetSessionTagsByEventId } from "../../api/tag";
import FilterSelect from "./FilterSelect";

// One page is enough for any realistic speaker roster
const SPEAKER_PAGE_SIZE = 500;

const ICON_SIZE = 16;

/**
 * The calendar's filters, one compact button each. `filters` holds arrays of
 * ids keyed by their url param; `onChange(key, values)` writes one of them
 * back. Speaker and tag options are only fetched once a dropdown is opened.
 */
export default function SessionFilters({
  eventId,
  locations,
  filters,
  onChange,
}) {
  const [optionsNeeded, setOptionsNeeded] = useState(false);
  const loadOptions = () => setOptionsNeeded(true);

  const { data: speakers, isFetching: speakersLoading } = useGetAllSpeakers(
    { eventId, limit: SPEAKER_PAGE_SIZE },
    { enabled: optionsNeeded }
  );
  const { data: tags, isFetching: tagsLoading } = useGetSessionTagsByEventId(
    eventId,
    { enabled: optionsNeeded }
  );

  const speakerOptions = useMemo(
    () =>
      (speakers?.data ?? []).map((speaker) => ({
        value: String(speaker.id),
        label: `${speaker.firstName} ${speaker.lastName}`,
      })),
    [speakers]
  );

  const tagOptions = useMemo(
    () =>
      (tags ?? []).map((tag) => ({ value: String(tag.id), label: tag.title })),
    [tags]
  );

  return (
    <Group gap="xs">
      <FilterSelect
        label="Locations"
        noun="locations"
        icon={<IconMapPin size={ICON_SIZE} />}
        value={filters.locationFilter}
        data={locations}
        onChange={(values) => onChange("locationFilter", values)}
      />
      <FilterSelect
        label="Speakers"
        noun="speakers"
        icon={<IconMicrophone size={ICON_SIZE} />}
        value={filters.speakerFilter}
        data={speakerOptions}
        loading={speakersLoading}
        onOpen={loadOptions}
        onChange={(values) => onChange("speakerFilter", values)}
      />
      <FilterSelect
        label="Tags"
        noun="tags"
        icon={<IconTag size={ICON_SIZE} />}
        value={filters.tagFilter}
        data={tagOptions}
        loading={tagsLoading}
        onOpen={loadOptions}
        onChange={(values) => onChange("tagFilter", values)}
      />
    </Group>
  );
}
